// @ts-nocheck
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import countryConfigData from '../contexts/country-config';

import enUsCommon from '../locales/en-US/common.json';
import enGbOverrides from '../locales/en-GB/common.json';
import enAuCommon from '../locales/en-AU/common.json';
import enCaCommon from '../locales/en-CA/common.json';
import frFrCommon from '../locales/fr-FR/common.json';
import frCaCommon from '../locales/fr-CA/common.json';
import deDeCommon from '../locales/de-DE/common.json';
import elGrCommon from '../locales/el-GR/common.json';
import esEsCommon from '../locales/es-ES/common.json';
import esMxCommon from '../locales/es-MX/common.json';
import hiInCommon from '../locales/hi-IN/common.json';
import itItCommon from '../locales/it-IT/common.json';
import ptBrCommon from '../locales/pt-BR/common.json';
import daDkCommon from '../locales/da-DK/common.json';
import nbNoCommon from '../locales/nb-NO/common.json';
import nlBeCommon from '../locales/nl-BE/common.json';
import plPlCommon from '../locales/pl-PL/common.json';
import roRoCommon from '../locales/ro-RO/common.json';
import srRsCommon from '../locales/sr-RS/common.json';

const enGbCommon = {
  ...enUsCommon,
  ...enGbOverrides,
};

const STATIC_RESOURCES = {
  en: { common: enUsCommon },
  'en-US': { common: enUsCommon },
  'en-GB': { common: enGbCommon },
  'en-AU': { common: enAuCommon },
  'en-CA': { common: enCaCommon },
  fr: { common: frFrCommon },
  'fr-FR': { common: frFrCommon },
  'fr-CA': { common: frCaCommon },
  de: { common: deDeCommon },
  'de-DE': { common: deDeCommon },
  el: { common: elGrCommon },
  'el-GR': { common: elGrCommon },
  es: { common: esEsCommon },
  'es-ES': { common: esEsCommon },
  'es-MX': { common: esMxCommon },
  'es-419': { common: esMxCommon },
  hi: { common: hiInCommon },
  'hi-IN': { common: hiInCommon },
  it: { common: itItCommon },
  'it-IT': { common: itItCommon },
  pt: { common: ptBrCommon },
  'pt-BR': { common: ptBrCommon },
  da: { common: daDkCommon },
  'da-DK': { common: daDkCommon },
  nb: { common: nbNoCommon },
  'nb-NO': { common: nbNoCommon },
  nl: { common: nlBeCommon },
  'nl-BE': { common: nlBeCommon },
  pl: { common: plPlCommon },
  'pl-PL': { common: plPlCommon },
  ro: { common: roRoCommon },
  'ro-RO': { common: roRoCommon },
  sr: { common: srRsCommon },
  'sr-RS': { common: srRsCommon },
};

let sharedInstance;
let sharedInitPromise;

const isBrowser = typeof window !== 'undefined';

const COUNTRY_CONFIG = countryConfigData.countries || {};
const SUPPORTED_COUNTRIES = Object.keys(COUNTRY_CONFIG);

const getCountryFromPathname = (pathname) => {
  const trimmed = (pathname || '').replace(/^\/+|\/+$/g, '');
  if (!trimmed) {
    return null;
  }
  const [firstSegment] = trimmed.split('/');
  if (!firstSegment) {
    return null;
  }
  return SUPPORTED_COUNTRIES.includes(firstSegment) ? firstSegment : null;
};

const getInitialLanguageFromPath = () => {
  if (!isBrowser) {
    return null;
  }
  const pathname = window.location?.pathname;
  const country = getCountryFromPathname(pathname);
  if (country && COUNTRY_CONFIG[country]) {
    return COUNTRY_CONFIG[country].language;
  }
  return null;
};

const createI18nInstance = () => {
  const instance = i18next.createInstance();

  instance.use(initReactI18next);

  if (isBrowser) {
    instance.use(LanguageDetector).use(Backend);
  }

  const pathLanguage = getInitialLanguageFromPath();

  const initOptions = {
    lng: pathLanguage || undefined,
    fallbackLng: 'en-US',
    supportedLngs: Object.keys(STATIC_RESOURCES),
    defaultNS: 'common',
    ns: ['common'],
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
    resources: STATIC_RESOURCES,
    initImmediate: false,
  };

  if (isBrowser) {
    initOptions.backend = {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    };
    // Configure language detection with custom cookie for cross-subdomain sharing
    // Cookie 'mtLang' is shared between www.magictableplanner.com and app.magictableplanner.com
    const isProduction = window.location.hostname.includes(
      'magictableplanner.com'
    );
    initOptions.detection = {
      order: ['querystring', 'cookie', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang', // Query parameter name (?lang=fr)
      lookupCookie: 'mtLang', // Custom cookie name for cross-subdomain persistence
      caches: ['localStorage', 'cookie'], // Persist to both localStorage and cookie
      cookieDomain: isProduction ? '.magictableplanner.com' : undefined, // Enable sharing across all subdomains in production
    };
  }

  sharedInitPromise = instance.init(initOptions).catch((error) => {
    if (process.env.NODE_ENV !== 'test') {
      console.error('[i18n] Failed to initialize i18next', error);
    }
  });

  return instance;
};

export const getI18n = () => {
  if (!sharedInstance) {
    sharedInstance = createI18nInstance();
  }
  return sharedInstance;
};

export const whenI18nReady = () => {
  if (!sharedInitPromise) {
    getI18n();
  }
  return sharedInitPromise;
};
